/**
 * Promise-based chat turns over Orbiee Agent WebSocket, shaped like the
 * HTTP `chat_vertex_detail_cart_payment` response where possible so
 * OrbieeFlow can stay one code path.
 */

import OrbieeChatSocket from "./orbieeChatSocket";
import { createAudioPlayer } from "./orbieeAudioPlayer";
import { USE_ORBIEE_WEBSOCKET_CHAT } from "../../config/apiConfig";
import { saveCheckoutPrefill } from "../../utils/checkoutPrefill";
import { hasWsAuthToken, requireWsAuth, WS_AUTH_ERROR_MESSAGE } from "./wsAuth";
import { toolResultToProductDetail } from "../../utils/productDetailFromToolResult";
import {
  bindUiSyncSocket,
  unbindUiSyncSocket,
  getScreenContext,
} from "../uiAgentSync";

/** Encode a Blob/File as raw base64 (no `data:*;base64,` prefix). */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    if (!blob) {
      reject(new Error("blobToBase64: blob required"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("blobToBase64: unexpected read result"));
        return;
      }
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () =>
      reject(reader.error || new Error("blobToBase64: read failed"));
    reader.readAsDataURL(blob);
  });
}

/** Whether STT text should trigger an agent turn (mirrors legacy ElevenLabs guards). */
export function isValidTranscription(text) {
  if (text == null) return false;
  const t = String(text).trim();
  if (!t) return false;
  if (t === "No speech recognized.") return false;
  if (/^\[.*\]$/.test(t)) return false;
  if (t.toLowerCase().replace(/\.$/, "") === "no speech recognized") return false;
  return true;
}

/** Soft voice failure: mic closed with silence / STT heard nothing. */
export const EMPTY_TRANSCRIPT_ERROR_CODE = "EMPTY_TRANSCRIPT";

export function isEmptyTranscriptError(err) {
  if (!err) return false;
  if (err.code === EMPTY_TRANSCRIPT_ERROR_CODE) return true;
  const msg = String(err.message || err || "").toLowerCase();
  return (
    msg.includes("empty transcript") ||
    msg.includes("no speech recognized") ||
    msg.includes("no speech")
  );
}

function makeEmptyTranscriptError(message) {
  const err = new Error(
    message || "voice: STT returned an empty transcript"
  );
  err.code = EMPTY_TRANSCRIPT_ERROR_CODE;
  err.isEmptyTranscript = true;
  return err;
}

function normalizeTranscriptData(data) {
  if (!data || typeof data !== "object") return null;
  const text =
    typeof data.text === "string"
      ? data.text.trim()
      : typeof data.transcription === "string"
        ? data.transcription.trim()
        : "";
  if (!isValidTranscription(text)) return null;
  return {
    text,
    language_code:
      typeof data.language_code === "string" && data.language_code.trim()
        ? data.language_code.trim()
        : null,
    audio_duration_secs:
      typeof data.audio_duration_secs === "number"
        ? data.audio_duration_secs
        : null,
    input_mode:
      typeof data.input_mode === "string" ? data.input_mode : "voice",
  };
}

async function computeDeviceFingerprint() {
  const nav = typeof navigator !== "undefined" ? navigator : {};
  const scr = typeof window !== "undefined" ? window.screen : {};
  const tz = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ""; }
  })();
  const raw = [
    nav.userAgent || "",
    nav.language || "",
    String(nav.hardwareConcurrency || ""),
    String(nav.deviceMemory || ""),
    String(nav.platform || ""),
    String(scr.width || ""),
    String(scr.height || ""),
    String(scr.colorDepth || ""),
    String(scr.pixelDepth || ""),
    tz,
  ].join("|");

  try {
    const buf = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(raw)
    );
    const hex = Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    // Format as UUID-like string for readability: 8-4-4-4-12
    return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20,32)}`;
  } catch {
    // SubtleCrypto unavailable (non-secure context) — fall back to random
    return (crypto.randomUUID?.() || `dev_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  }
}

/** Device-specific ID derived from browser/hardware fingerprint, cached in localStorage. */
export async function getOrbieeDeviceId() {
  const cached = localStorage.getItem("orbiee_device_id");
  if (cached) return cached;
  const id = await computeDeviceFingerprint();
  localStorage.setItem("orbiee_device_id", id);
  return id;
}

let socketState = {
  socket: null,
  identityKey: "",
};

/**
 * Backend connect-greeting: `{ type: "message", data: { source: "assistant", content } }`
 * sent while no turn is in flight. Cached so chat/avatar can prefer it over
 * hardcoded copy, without mixing it into a later chat/voice/avatar turn.
 */
const welcomeState = {
  text: null,
  pending: true,
  listeners: new Set(),
};
/** @type {null | (() => void)} */
let unbindWelcome = null;

function extractAssistantWelcomeContent(data) {
  if (!data || typeof data !== "object") return null;
  if (data.source !== "assistant") return null;
  const text = String(data.content ?? "").trim();
  return text || null;
}

function notifyBackendWelcome(text) {
  welcomeState.text = text;
  welcomeState.pending = false;
  [...welcomeState.listeners].forEach((cb) => {
    try {
      cb(text);
    } catch (err) {
      console.warn("[WS] backend welcome listener threw", err);
    }
  });
}

/** @returns {boolean} true when this frame was consumed as the session welcome */
function consumeSessionWelcome(data) {
  if (!welcomeState.pending) return false;
  const text = extractAssistantWelcomeContent(data);
  if (!text) return false;
  notifyBackendWelcome(text);
  return true;
}

function resetWelcomeState() {
  welcomeState.text = null;
  welcomeState.pending = true;
}

function bindWelcomeSocket(socket) {
  unbindWelcomeSocket();
  welcomeState.pending = true;

  const offOpen = socket.on("open", () => {
    // New connection may send a new greeting; keep last text until it arrives
    // so late subscribers still have a fallback during reconnect.
    welcomeState.pending = true;
  });
  const offMsg = socket.on("message", (data) => {
    if (socket._streaming) return;
    consumeSessionWelcome(data);
  });

  unbindWelcome = () => {
    offOpen();
    offMsg();
  };
}

function unbindWelcomeSocket() {
  if (!unbindWelcome) return;
  try {
    unbindWelcome();
  } catch {
    /* socket may already be destroyed */
  }
  unbindWelcome = null;
}

/** Latest backend welcome text, or null if none has arrived this session. */
export function getOrbieeBackendWelcomeMessage() {
  const text = welcomeState.text;
  if (text == null) return null;
  const trimmed = String(text).trim();
  return trimmed || null;
}

/**
 * Subscribe to the backend welcome. Fires immediately if one is already cached.
 * @param {(text: string) => void} cb
 * @returns {() => void} unsubscribe
 */
export function subscribeOrbieeBackendWelcome(cb) {
  if (typeof cb !== "function") return () => {};
  welcomeState.listeners.add(cb);
  const existing = getOrbieeBackendWelcomeMessage();
  if (existing) {
    try {
      cb(existing);
    } catch (err) {
      console.warn("[WS] backend welcome listener threw", err);
    }
  }
  return () => {
    welcomeState.listeners.delete(cb);
  };
}

/**
 * Resolve when the backend welcome arrives, or null if it does not within timeoutMs.
 * @param {number} [timeoutMs=2500]
 * @returns {Promise<string|null>}
 */
export function waitForOrbieeBackendWelcome(timeoutMs = 2500) {
  const existing = getOrbieeBackendWelcomeMessage();
  if (existing) return Promise.resolve(existing);

  const ms = Number(timeoutMs);
  const waitMs = Number.isFinite(ms) && ms > 0 ? ms : 2500;

  return new Promise((resolve) => {
    let done = false;
    /** @type {() => void} */
    let unsub = () => {};

    const finish = (val) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      unsub();
      resolve(val);
    };

    unsub = subscribeOrbieeBackendWelcome((text) => {
      const trimmed = String(text ?? "").trim();
      if (trimmed) finish(trimmed);
    });

    const timer = setTimeout(() => finish(null), waitMs);

    const raced = getOrbieeBackendWelcomeMessage();
    if (raced) finish(raced);
  });
}

/**
 * Streamed voice-reply playback (backend `audio_start` / binary / `audio_end`).
 *
 * One player for the app's lifetime: audio belongs to the session, not to a
 * single turn (playback routinely outlives the `completion` frame), and UI
 * subscribers registered once must stay valid across logout / reconnect. It
 * is therefore stopped, never destroyed, when a session ends.
 */
let audioPlayer = null;
/** Teardown for the audio listeners bound to the current socket instance. */
let unbindAudio = null;

function getAudioPlayer() {
  if (!audioPlayer) audioPlayer = createAudioPlayer();
  return audioPlayer;
}

/** Route a socket's audio frames into the shared player. */
function bindAudioSocket(socket) {
  unbindAudioSocket();
  const player = getAudioPlayer();
  const offStart = socket.on("audio-start", (data) => player.start(data || {}));
  const offChunk = socket.on("audio-chunk", (buf) => player.onChunk(buf));
  const offEnd = socket.on("audio-end", (data) => player.end(data || {}));
  // Streams do not resume across reconnects — drop whatever was in flight.
  const offClose = socket.on("close", () => player.stop());
  unbindAudio = () => {
    offStart();
    offChunk();
    offEnd();
    offClose();
  };
}

function unbindAudioSocket() {
  if (unbindAudio) {
    try {
      unbindAudio();
    } catch {
      /* socket may already be destroyed */
    }
    unbindAudio = null;
  }
}

/**
 * Prepare audio output. Browsers block playback until the user interacts,
 * so call this from a user gesture (the mic tap that precedes a voice turn).
 * Safe to call repeatedly.
 */
export function unlockOrbieeVoicePlayback() {
  try {
    getAudioPlayer().unlock();
  } catch (err) {
    console.warn("[WS] audio unlock failed", err);
  }
}

/** Stop streamed voice playback immediately (barge-in / stop / chat close). */
export function stopOrbieeVoicePlayback() {
  if (!audioPlayer) return;
  try {
    audioPlayer.stop();
  } catch {
    /* nothing playing */
  }
}

/**
 * Observe playback state: the callback receives "playing" or "idle".
 * "idle" fires when the audio has actually finished sounding, which is later
 * than `audio_end` because the server streams ahead of real time.
 *
 * @param {(state: "playing"|"idle") => void} cb
 * @returns {() => void} unsubscribe
 */
export function subscribeOrbieeVoicePlayback(cb) {
  return getAudioPlayer().subscribe(cb);
}

/** True while streamed voice audio is playing or still scheduled. */
export function isOrbieeVoicePlaying() {
  return audioPlayer ? audioPlayer.isActive() : false;
}

function getOrCreateSocket(userId, deviceId, queryParams) {
  const identityKey = `${String(userId)}:${String(deviceId)}`;

  // Session identity is user + device only. Per-turn query params
  // (language, conversation_id, intent override) used to be part of the
  // key, which destroyed the live socket on every prompt.
  if (socketState.socket && socketState.identityKey !== identityKey) {
    try {
      socketState.socket.destroy();
    } catch {
      /* ignore */
    }
    unbindUiSyncSocket();
    unbindAudioSocket();
    unbindWelcomeSocket();
    resetWelcomeState();
    stopOrbieeVoicePlayback();
    socketState.socket = null;
  }

  if (!socketState.socket) {
    socketState.socket = new OrbieeChatSocket({
      userId: String(userId),
      deviceId: String(deviceId),
      queryParams: queryParams || undefined,
      autoReconnect: true,
    });
    socketState.identityKey = identityKey;
    // UI → Agent sync sends `ui_event` frames over this same socket.
    bindUiSyncSocket(socketState.socket);
    // Streamed voice replies arrive on this same socket.
    bindAudioSocket(socketState.socket);
    bindWelcomeSocket(socketState.socket);
  } else if (queryParams && typeof queryParams === "object") {
    // Keep the live connection. Merge latest ctx_* so a later reconnect
    // resumes with current conversation / language context.
    socketState.socket.queryParams = {
      ...(socketState.socket.queryParams || {}),
      ...queryParams,
    };
  }

  return socketState.socket;
}

/** Close the shared agent socket (e.g. on full logout before redirect). */
export function resetOrbieeChatSocket() {
  if (socketState.socket) {
    try {
      socketState.socket.destroy();
    } catch {
      /* ignore */
    }
  }
  unbindUiSyncSocket();
  unbindAudioSocket();
  unbindWelcomeSocket();
  resetWelcomeState();
  stopOrbieeVoicePlayback();
  socketState.socket = null;
  socketState.identityKey = "";
}

function waitForOpen(socket, timeoutMs = 25_000) {
  if (socket.isConnected) return Promise.resolve();
  return new Promise((resolve, reject) => {
    let done = false;
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      cleanup();
      reject(new Error("WebSocket connection timed out"));
    }, timeoutMs);
    const cleanup = () => {
      clearTimeout(timer);
      offOpen();
      offClose();
      offFail();
    };
    const settleOk = () => {
      if (done) return;
      done = true;
      cleanup();
      resolve();
    };
    const settleErr = (err) => {
      if (done) return;
      done = true;
      cleanup();
      reject(err);
    };
    const offOpen = socket.on("open", settleOk);
    // Close is not fatal while auto-reconnect is in progress — wait for
    // the next "open" (or timeout / reconnect-failed).
    const offClose = socket.on("close", (info) => {
      if (done || socket.isConnected) return;
      queueMicrotask(() => {
        if (done || socket.isConnected) return;
        if (socket.isConnecting || socket.isReconnecting) return;
        const reason =
          typeof info?.reason === "string" && info.reason
            ? info.reason
            : "WebSocket closed before connected";
        settleErr(new Error(reason));
      });
    });
    const offFail = socket.on("reconnect-failed", () => {
      settleErr(new Error("WebSocket reconnect failed"));
    });
    if (socket.isConnected) settleOk();
  });
}

function safeParseJson(str) {
  if (typeof str !== "string") return null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

function parseToolResultPayload(tr) {
  if (!tr || typeof tr !== "object") return null;
  const raw = tr.result ?? tr.output ?? tr.payload;
  if (typeof raw === "string") return safeParseJson(raw);
  if (raw != null && typeof raw === "object") return raw;
  return null;
}

/** Unwrap `{ data: { tool, result } }` style payloads from FastAPI/agent. */
function coerceToolResultRecord(frameData) {
  if (frameData == null || typeof frameData !== "object") return null;
  const fd = /** @type {Record<string, unknown>} */ (frameData);
  const hasTool = Object.prototype.hasOwnProperty.call(fd, "tool");
  const hasBody =
    Object.prototype.hasOwnProperty.call(fd, "result") ||
    Object.prototype.hasOwnProperty.call(fd, "output") ||
    Object.prototype.hasOwnProperty.call(fd, "payload");
  if (hasTool && hasBody) return fd;
  const inner = fd.data;
  if (inner && typeof inner === "object") {
    return coerceToolResultRecord(inner);
  }
  return fd;
}

/**
 * Map `get_product_details` tool JSON → shape expected by ChatBot / ProductCard.
 *
 * @param {Record<string, unknown>} parsed
 * @returns {Record<string, unknown> | null}
 */
function catalogProductFromGetProductDetails(parsed) {
  if (!parsed || typeof parsed !== "object") return null;
  const pidRaw =
    parsed.id ?? parsed.product_id ?? parsed.article_id ?? parsed.sku ?? null;
  const pid =
    pidRaw != null && String(pidRaw).trim() !== ""
      ? String(pidRaw).trim()
      : null;
  if (!pid) return null;

  const price =
    typeof parsed.price === "number"
      ? parsed.price
      : parseFloat(String(parsed.price ?? 0));
  const unitPrice = Number.isFinite(price) ? price : 0;

  const currencyRaw = parsed.currency;
  const currency =
    typeof currencyRaw === "string" && currencyRaw.trim()
      ? currencyRaw.trim()
      : "USD";

  const uri =
    (typeof parsed.image_uri === "string" && parsed.image_uri) ||
    (typeof parsed.uri === "string" && parsed.uri) ||
    "";
  const imgs = Array.isArray(parsed.images)
    ? parsed.images.filter((u) => typeof u === "string")
    : [];
  const img = uri || (imgs.length ? imgs[0] : "") || "";

  const attrs =
    parsed.attributes != null &&
    typeof parsed.attributes === "object" &&
    !Array.isArray(parsed.attributes)
      ? /** @type {Record<string, unknown>} */ (parsed.attributes)
      : {};

  /** @type {Record<string, unknown>} */
  const out = {
    id: pid,
    title:
      typeof parsed.title === "string"
        ? parsed.title
        : String(parsed.title ?? ""),
    description:
      typeof parsed.description === "string" ? parsed.description : "",
    price: unitPrice,
    currency,
    price_info: { price: unitPrice, currencyCode: currency },
    images: imgs.length ? imgs : img ? [img] : [],
    image_uri: img,
    image: img,
    attributes: attrs,
  };

  if (Array.isArray(parsed.available_sizes)) {
    out.available_sizes = parsed.available_sizes;
  }

  return out;
}

/**
 * Map cart tool `item` / line objects (product_id, image_uri, …) → ChatBot catalogue shape.
 *
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown> | null}
 */
function cartToolItemToProductShape(item) {
  if (!item || typeof item !== "object") return null;
  const pidRaw =
    item.product_id ?? item.article_id ?? item.id ?? item.sku ?? null;
  const pid =
    pidRaw != null && String(pidRaw).trim() !== ""
      ? String(pidRaw).trim()
      : null;
  if (!pid) return null;

  const sizeRaw = item.size;
  const size =
    sizeRaw != null && sizeRaw !== ""
      ? Array.isArray(sizeRaw)
        ? String(sizeRaw[0])
        : String(sizeRaw)
      : null;

  const qty =
    typeof item.quantity === "number"
      ? item.quantity
      : parseInt(String(item.quantity ?? ""), 10) || 1;

  const price =
    typeof item.price === "number"
      ? item.price
      : parseFloat(String(item.price ?? 0)) || 0;

  const currency =
    item.currency != null && item.currency !== ""
      ? String(item.currency)
      : "USD";

  const img =
    (typeof item.image_uri === "string" && item.image_uri) ||
    (typeof item.uri === "string" && item.uri) ||
    "";

  /** Top-level price for ProductCard; price_info for addToCart / HTTP parity */
  return {
    id: pid,
    title: item.title != null ? String(item.title) : "",
    price,
    currency,
    price_info: { price, currencyCode: currency },
    images: img ? [img] : [],
    image_uri: img,
    image: img,
    attributes: size ? { size: [size] } : {},
    size,
    quantity: qty,
  };
}

/**
 * Map WishlistSummary.items (backend shape) → frontend wishlist item shape.
 * Used to surface the authoritative post-add/post-remove wishlist snapshot so
 * ChatBot can call replaceWishlistFromAgent instead of a redundant API call.
 *
 * @param {unknown[]} rawItems
 * @returns {Record<string, unknown>[]}
 */
function shapeWishlistItems(rawItems) {
  if (!Array.isArray(rawItems)) return [];
  return rawItems.map((it) => {
    if (!it || typeof it !== "object") return null;
    const item = /** @type {Record<string, unknown>} */ (it);
    const img =
      (typeof item.image_uri === "string" && item.image_uri) ||
      (typeof item.image_url === "string" && item.image_url) ||
      (typeof item.image === "string" && item.image) ||
      (typeof item.uri === "string" && item.uri) ||
      "";
    const id = item.product_id ?? item.article_id ?? item.id ?? null;
    // Support backend-specific name fields (enhanced_prod_name) and price fields (discounted_price)
    const title =
      (typeof item.enhanced_prod_name === "string" && item.enhanced_prod_name) ||
      (typeof item.product_title === "string" && item.product_title) ||
      (typeof item.title === "string" && item.title) ||
      (typeof item.name === "string" && item.name) ||
      "";
    const rawPrice = item.discounted_price ?? item.price;
    const price =
      typeof rawPrice === "number" ? rawPrice : parseFloat(String(rawPrice ?? 0)) || 0;
    return {
      id,
      article_id: id,
      product_id: id,
      title,
      name: title,
      price,
      currency: typeof item.currency === "string" && item.currency ? item.currency : "USD",
      image_uri: img,
      image: img,
      image_url: typeof item.image_url === "string" ? item.image_url : img,
      images: img ? [img] : [],
    };
  }).filter(Boolean);
}

/** Compare tool ids across backends (`View_Cart`, `view-cart`, `view_cart`). */
function normalizeAgentToolName(name) {
  if (typeof name !== "string") return "";
  const s = name.trim().toLowerCase();
  if (!s) return "";
  return s.replace(/-/g, "_");
}

/**
 * Apply HTTP-shaped `view_cart` fields to `merged` from inner tool JSON (`items`, `total`, …).
 *
 * @param {Record<string, unknown>} merged
 * @param {Record<string, unknown>} parsed
 */
function applyViewCartIntentToMerged(merged, parsed) {
  const prevAction =
    merged.action != null &&
    typeof merged.action === "object" &&
    !Array.isArray(merged.action)
      ? /** @type {Record<string, unknown>} */ ({ ...merged.action })
      : {};

  /** @type {Record<string, unknown>[]} */
  const cart_data = [];
  const nestedCart =
    parsed.cart != null && typeof parsed.cart === "object" && !Array.isArray(parsed.cart)
      ? parsed.cart : null;
  const rawItems = Array.isArray(parsed.items)
    ? parsed.items
    : (nestedCart && Array.isArray(nestedCart.items) ? nestedCart.items : []);
  for (const line of rawItems) {
    if (!line || typeof line !== "object") continue;
    const shaped = cartToolItemToProductShape(
      /** @type {Record<string, unknown>} */ (line)
    );
    if (shaped) cart_data.push(shaped);
  }

  const totalRaw = parsed.total ?? nestedCart?.total;
  const total_value =
    typeof totalRaw === "number" && Number.isFinite(totalRaw)
      ? totalRaw
      : parseFloat(String(totalRaw ?? 0)) || 0;

  const countRaw = parsed.items_count ?? nestedCart?.items_count;
  const distinct_product_count =
    typeof countRaw === "number" && Number.isFinite(countRaw)
      ? Math.max(0, Math.floor(countRaw))
      : cart_data.length;

  merged.intent = ["view_cart"];
  merged.cart_view = true;
  merged.action = {
    ...prevAction,
    type: "VIEW_CART",
    status: "SUCCESS",
    cart_data,
    total_value,
    distinct_product_count,
  };
}

/**
 * Map `start_virtual_try_on` tool JSON → HTTP-shaped intent for OrbieeFlow
 * (same session keys as ChatBot `applyVirtualTryOnSessionFromChatResponse`).
 */
function applyVirtualTryOnIntentToMerged(merged, parsed) {
  if (!parsed || typeof parsed !== "object") return;
  const err = parsed.error;
  if (
    err != null &&
    err !== "" &&
    String(err).toLowerCase() !== "null" &&
    String(err).toLowerCase() !== "none"
  ) {
    return;
  }

  const stRaw = parsed.status;
  const st =
    typeof stRaw === "string" ? stRaw.trim().toLowerCase() : "";
  if (st !== "started" && st !== "success" && st !== "ok") return;

  const productIdRaw = parsed.product_id;
  const productId =
    productIdRaw != null ? String(productIdRaw).trim() : "";
  if (!productId) return;

  const prevAction =
    merged.action != null &&
    typeof merged.action === "object" &&
    !Array.isArray(merged.action)
      ? /** @type {Record<string, unknown>} */ ({ ...merged.action })
      : {};

  merged.intent = ["virtual_try_on"];
  merged.action = {
    ...prevAction,
    type: "START_VIRTUAL_TRY_ON",
    status: "SUCCESS",
    product_id: productId,
    ...(parsed.product_title != null
      ? { product_title: String(parsed.product_title) }
      : {}),
    ...(parsed.image_uri != null
      ? { image_uri: String(parsed.image_uri) }
      : {}),
    ...(parsed.uri != null ? { uri: String(parsed.uri) } : {}),
  };
}

/** Tools that return a browseable product list (search, new arrivals, recommendations…). */
const PRODUCT_LIST_TOOL_NAMES = new Set([
  "search_products",
  "product_search",
  "get_products",
  "get_product_list",
  "get_new_arrivals",
  "new_arrivals",
  "get_recommended_products",
  "recommended_products",
  "get_recommendations",
  "get_similar_products",
  "similar_products",
  "get_on_sale_products",
  "on_sale_products",
  "get_sale_products",
  "filter_products",
  "get_featured_products",
  "featured_products",
  "get_all_products",
  "get_category_products",
  "category_products",
  "show_complements",
]);

/**
 * Merge tool_result payloads in order. Later tools override scalars.
 * `products` uses the last non-empty array from any tool (Vertex shape),
 * or a single catalogue object from {@code get_product_details}.
 */
function mergeToolJson(toolResults) {
  /** @type {Record<string, unknown>} */
  const merged = {};
  let products = [];

  for (const raw of toolResults || []) {
    const row = coerceToolResultRecord(raw) ?? raw;
    if (!row || typeof row !== "object") continue;

    const parsed = parseToolResultPayload(row);
    if (
      parsed == null ||
      typeof parsed !== "object" ||
      Array.isArray(parsed)
    ) {
      continue;
    }

    if (Array.isArray(parsed.products) && parsed.products.length) {
      products = parsed.products;
    }

    for (const key of Object.keys(parsed)) {
      if (key === "products") continue;
      merged[key] = parsed[key];
    }
  }

  for (const raw of toolResults || []) {
    const row = coerceToolResultRecord(raw) ?? raw;
    if (!row || typeof row !== "object") continue;

    // Extract tool name from the outer frame before parsing the result body,
    // so stateless tools (clear_cart) work even when the result payload is a
    // plain string, null, or otherwise non-object.
    const earlyToolNorm = normalizeAgentToolName(
      typeof row.tool === "string" ? row.tool : ""
    );

    // clear_cart / view_wishlist carry no meaningful result body (or the
    // backend omits tool_result entirely).  Fire here before the
    // parsed-payload guard so synthetic entries aren't silently skipped.
    if (earlyToolNorm === "clear_cart") {
      merged.intent = ["clear_cart"];
      merged._is_clear_cart = true;
      merged.action = {
        ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
        type: "CLEAR_CART",
        status: "SUCCESS",
        cart_data: [],
      };
      merged.cart_view = false;
      console.debug("[WS] clear_cart triggered (early path)");
    }

    // view_wishlist: if no real tool_result payload arrived (synthetic entry),
    // set an empty wishlist action so applyWishlistActionFromResponse can
    // clear stale frontend state.  Skip if a real VIEW_WISHLIST action with
    // actual items was already populated by a real tool_result in pass-2.
    if (earlyToolNorm === "view_wishlist" || earlyToolNorm === "get_wishlist") {
      const existingWlAction =
        merged.action != null &&
        typeof merged.action === "object" &&
        /** @type {any} */ (merged.action).type === "VIEW_WISHLIST" &&
        Array.isArray(/** @type {any} */ (merged.action).wishlist_data) &&
        /** @type {any} */ (merged.action).wishlist_data.length > 0;
      if (!existingWlAction) {
        merged.intent = ["view_wishlist"];
        merged.action = {
          ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
          type: "VIEW_WISHLIST",
          status: "SUCCESS",
          wishlist_data:
            Array.isArray(/** @type {any} */ (merged.action)?.wishlist_data)
              ? /** @type {any} */ (merged.action).wishlist_data
              : [],
          items_count:
            /** @type {any} */ (merged.action)?.items_count ?? 0,
        };
        console.debug("[WS] view_wishlist early path (synthetic or null payload)");
      }
    }

    const parsed = parseToolResultPayload(row);
    if (
      parsed == null ||
      typeof parsed !== "object" ||
      Array.isArray(parsed)
    ) {
      continue;
    }

    const toolNameRaw =
      typeof row.tool === "string"
        ? row.tool
        : typeof parsed.tool === "string"
          ? parsed.tool
          : typeof parsed.tool_name === "string"
            ? parsed.tool_name
            : undefined;
    const toolNorm = normalizeAgentToolName(
      /** @type {string | undefined} */ (toolNameRaw)
    );

    if (toolNorm === "get_product_details" || toolNorm === "get_product_detail") {
      const parsedRecord = /** @type {Record<string, unknown>} */ (parsed);
      const shaped = catalogProductFromGetProductDetails(parsedRecord);
      const productDetail = toolResultToProductDetail(parsedRecord);
      if (shaped) {
        products = [shaped];
        merged.intent = ["detail_page"];
        merged.action = {
          ...(merged.action != null &&
          typeof merged.action === "object" &&
          !Array.isArray(merged.action)
            ? /** @type {Record<string, unknown>} */ (merged.action)
            : {}),
          type: "DETAIL_PAGE_VIEW",
          status: "SUCCESS",
          product: shaped,
          ...(productDetail ? { product_detail: productDetail } : {}),
        };
        if (productDetail) {
          merged.product_detail = productDetail;
        }
      }
    }

    const prevAction =
      merged.action != null &&
      typeof merged.action === "object" &&
      !Array.isArray(merged.action)
        ? /** @type {Record<string, unknown>} */ ({ ...merged.action })
        : {};

    if (toolNorm === "add_to_cart") {
      const st = parsed.status;
      // Accept "added", "success", "ok" — also treat any response with item/cart data as success
      const isAddSuccess =
        st === "added" || st === "success" || st === "ok" ||
        (!st && (parsed.item != null || parsed.cart != null));

      if (isAddSuccess) {
        /** @type {unknown[]} */
        const rawPieces = [];
        if (parsed.item != null) rawPieces.push(parsed.item);
        if (Array.isArray(parsed.items)) rawPieces.push(...parsed.items);

        /** @type {Record<string, unknown>[]} */
        const shapedPieces = [];
        for (const raw of rawPieces) {
          if (!raw || typeof raw !== "object") continue;
          const piece = cartToolItemToProductShape(
            /** @type {Record<string, unknown>} */ (raw)
          );
          if (piece) shapedPieces.push(piece);
        }

        // Accumulate products across multiple add_to_cart tool_results so that
        // when the backend fires one tool_call per item (e.g. "add 3 items"),
        // all items are preserved — not just the last one.
        const existingProducts = Array.isArray(prevAction.product)
          ? prevAction.product
          : prevAction.product != null
            ? [prevAction.product]
            : [];
        const allProducts = [...existingProducts, ...shapedPieces];

        const nextProduct =
          allProducts.length === 0
            ? null
            : allProducts.length === 1
              ? allProducts[0]
              : allProducts;

        merged.intent = ["add_to_cart"];
        merged.action = {
          ...prevAction,
          status: "SUCCESS",
          type: "ADD_TO_CART",
          ...(nextProduct != null ? { product: nextProduct } : {}),
          // Always prefer the latest cart snapshot (most items); accumulate avoids
          // losing earlier items when no snapshot is provided.
          ...(parsed.cart != null ? { cart: parsed.cart } : {}),
        };

        if (shapedPieces.length) {
          // Accumulate across tool_results instead of overwriting with latest only
          products = [...products, ...shapedPieces];
        }
      } else if (st === "needs_size" || st === "size_suggested") {
        merged.intent = ["add_to_cart"];
        merged.action = {
          ...prevAction,
          status: "PENDING_SIZE_CONFIRMATION",
          ...(parsed.product_id != null
            ? { product_id: parsed.product_id }
            : {}),
          ...(parsed.product_title != null
            ? { product_title: parsed.product_title }
            : {}),
          ...(parsed.available_sizes != null
            ? { available_sizes: parsed.available_sizes }
            : {}),
          ...(parsed.default_size != null
            ? { default_size: parsed.default_size }
            : {}),
          ...(parsed.suggested_size != null
            ? { suggested_size: parsed.suggested_size }
            : {}),
          ...(parsed.suggestion_reason != null
            ? { suggestion_reason: parsed.suggestion_reason }
            : {}),
        };
      }
    }

    if (toolNorm === "view_cart") {
      applyViewCartIntentToMerged(
        /** @type {Record<string, unknown>} */ (merged),
        /** @type {Record<string, unknown>} */ (parsed)
      );
    }

    if (toolNorm === "add_to_wishlist") {
      const st = typeof parsed.status === "string" ? parsed.status.toLowerCase().trim() : "";

      // Pre-extract wishlist snapshot BEFORE the status guard so we can use
      // its presence as a success signal (backend may omit the status field).
      const wishlistSnap = parsed.wishlist;
      const shapedSnap =
        wishlistSnap != null &&
        typeof wishlistSnap === "object" &&
        Array.isArray(/** @type {any} */ (wishlistSnap).items)
          ? shapeWishlistItems(/** @type {any} */ (wishlistSnap).items)
          : null;

      // "already_in_wishlist" treated same as "added" — item IS in wishlist, confirm local state.
      // Also fire when backend sends wishlist.items even without an explicit status field.
      const isSuccess =
        st === "added" || st === "already_in_wishlist" || st === "success" || st === "ok";

      if (isSuccess || shapedSnap != null) {
        // Backend nests product data inside parsed.item (WishlistItem shape)
        const wishlistItem =
          parsed.item != null && typeof parsed.item === "object" ? parsed.item : parsed;
        const img =
          (typeof wishlistItem.image_uri === "string" && wishlistItem.image_uri) ||
          (typeof wishlistItem.uri === "string" && wishlistItem.uri) ||
          (typeof parsed.image_uri === "string" && parsed.image_uri) ||
          "";
        const product = {
          id: wishlistItem.product_id ?? wishlistItem.article_id ?? wishlistItem.id ??
              parsed.article_id ?? parsed.product_id ?? parsed.id,
          title: wishlistItem.title ?? parsed.product_title ?? parsed.title ?? "",
          price: typeof wishlistItem.price === "number"
            ? wishlistItem.price
            : parseFloat(String(wishlistItem.price ?? parsed.price ?? 0)) || 0,
          currency: wishlistItem.currency ?? parsed.currency ?? "USD",
          image_uri: img,
          image: img,
          images: img ? [img] : [],
        };
        // If product id is still null but snapshot has items, take id from the last added item
        if (!product.id && shapedSnap && shapedSnap.length > 0) {
          product.id = shapedSnap[shapedSnap.length - 1].id;
        }
        console.debug("[WS] add_to_wishlist parsed:", {
          st, product, item: parsed.item,
          wishlistSnap: shapedSnap ? `${shapedSnap.length} items` : "none",
        });
        merged.intent = ["add_to_wishlist"];
        merged.action = {
          ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
          type: "ADD_TO_WISHLIST",
          status: "SUCCESS",
          product,
          article_id: product.id,
          ...(shapedSnap != null
            ? {
                wishlist_data: shapedSnap,
                items_count:
                  /** @type {any} */ (wishlistSnap).items_count ??
                  shapedSnap.length,
              }
            : {}),
        };
      }
    }

    if (toolNorm === "view_wishlist" || toolNorm === "get_wishlist") {
      // Backend view_wishlist returns one of:
      //   { items: [...], items_count }          — flat
      //   { wishlist: { items: [...], items_count } } — nested object
      //   { wishlist: [...] }                    — legacy flat array
      const nestedWishlist =
        parsed.wishlist != null &&
        typeof parsed.wishlist === "object" &&
        !Array.isArray(parsed.wishlist)
          ? /** @type {any} */ (parsed.wishlist)
          : null;
      const rawItems = Array.isArray(parsed.items)
        ? parsed.items
        : nestedWishlist && Array.isArray(nestedWishlist.items)
          ? nestedWishlist.items
          : Array.isArray(parsed.wishlist)
            ? parsed.wishlist
            : [];
      const wishlist_data = rawItems.map((it) => {
        const img =
          (typeof it.image_uri === "string" && it.image_uri) ||
          (typeof it.image_url === "string" && it.image_url) ||
          (typeof it.image === "string" && it.image) ||
          (typeof it.uri === "string" && it.uri) ||
          "";
        return {
          // Backend uses product_id (WishlistItem); article_id is legacy alias
          id: it.product_id ?? it.article_id ?? it.id,
          article_id: it.product_id ?? it.article_id ?? it.id,
          product_id: it.product_id ?? it.article_id ?? it.id,
          title: it.product_title ?? it.title ?? it.name ?? "",
          name: it.product_title ?? it.title ?? it.name ?? "",
          price: typeof it.price === "number" ? it.price : parseFloat(String(it.price ?? 0)) || 0,
          currency: it.currency ?? "USD",
          image_uri: img,
          image: img,
          images: img ? [img] : [],
        };
      });
      const backendCount =
        nestedWishlist?.items_count ??
        parsed.items_count ??
        wishlist_data.length;
      console.debug("[WS] view_wishlist parsed:", { count: wishlist_data.length, backendCount, wishlist_data });
      merged.intent = ["view_wishlist"];
      merged.action = {
        ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
        type: "VIEW_WISHLIST",
        status: "SUCCESS",
        wishlist_data,
        items_count: backendCount,
      };
    }

    if (toolNorm === "remove_from_wishlist") {
      const st = typeof parsed.status === "string" ? parsed.status.toLowerCase().trim() : "";

      // Pre-extract wishlist snapshot so its presence can signal success even without status field
      const wishlistSnapRm = parsed.wishlist;
      const shapedSnapRm =
        wishlistSnapRm != null &&
        typeof wishlistSnapRm === "object" &&
        Array.isArray(/** @type {any} */ (wishlistSnapRm).items)
          ? shapeWishlistItems(/** @type {any} */ (wishlistSnapRm).items)
          : null;

      const isSuccessRm = st === "removed" || st === "success" || st === "ok" || st === "deleted" || st === "item_removed";

      if (isSuccessRm || shapedSnapRm != null) {
        // Backend nests removed item inside parsed.item (WishlistItem shape)
        const wishlistItem =
          parsed.item != null && typeof parsed.item === "object" ? parsed.item : parsed;
        const articleId =
          wishlistItem.product_id ?? wishlistItem.article_id ?? wishlistItem.id ??
          parsed.article_id ?? parsed.product_id ?? parsed.id;
        console.debug("[WS] remove_from_wishlist parsed:", {
          st, articleId, item: parsed.item,
          wishlistSnap: shapedSnapRm ? `${shapedSnapRm.length} items` : "none",
        });
        merged.intent = ["remove_from_wishlist"];
        merged.action = {
          ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
          type: "REMOVE_FROM_WISHLIST",
          status: "SUCCESS",
          article_id: articleId,
          ...(shapedSnapRm != null ? { wishlist_data: shapedSnapRm } : {}),
        };
      }
    }

    if (toolNorm === "remove_from_cart") {
      const st = parsed.status;
      // Accept common success variants; also treat any response with a cart snapshot as success
      const isRemoveSuccess =
        st === "removed" || st === "decremented" || st === "success" || st === "ok" ||
        (parsed.cart != null && st !== "needs_size" && st !== "not_in_cart");
      if (isRemoveSuccess) {
        // Backend nests the removed item inside parsed.item — extract id from there too
        const removedItem =
          parsed.item != null && typeof parsed.item === "object" ? parsed.item : null;
        const articleId =
          parsed.article_id ?? parsed.product_id ?? parsed.id ??
          removedItem?.product_id ?? removedItem?.article_id ?? removedItem?.id ?? null;
        // "decremented" = quantity reduced by 1; anything else = full remove
        const removeAction = st === "decremented" ? "DECREMENT" : "REMOVE";
        // Backend returns updated cart snapshot in parsed.cart — let tryReplaceCartFromAgentAction sync it
        merged.intent = ["remove_from_cart"];
        merged.action = {
          ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
          type: "REMOVE_FROM_CART",
          status: "SUCCESS",
          article_id: articleId,
          remove_action: removeAction,
          ...(removedItem ? { item: cartToolItemToProductShape(removedItem) } : {}),
          ...(parsed.cart != null ? { cart: parsed.cart } : {}),
        };
        console.debug("[WS] remove_from_cart parsed:", { st, articleId, removeAction, cart: parsed.cart });
      } else if (st === "needs_size") {
        // Multiple sizes of same product in cart — ask user which to remove
        merged.intent = ["remove_from_cart"];
        merged.action = {
          ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
          type: "REMOVE_FROM_CART",
          status: "PENDING_SIZE_CONFIRMATION",
          product_id: parsed.product_id,
          product_title: parsed.product_title,
          available_sizes: parsed.available_sizes,
        };
      } else if (st === "not_in_cart") {
        merged.intent = ["remove_from_cart"];
        merged.action = {
          ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
          type: "REMOVE_FROM_CART",
          status: "NOT_IN_CART",
        };
      }
    }

    // clear_cart is handled before parsed-payload guard above (earlyToolNorm path)

    if (toolNorm === "prepare_billing_confirmation") {
      const st = parsed.status;
      if (st === "awaiting_confirmation") {
        // Shape cart items for UI and navigation
        const cartItems = [];
        if (parsed.cart && Array.isArray(parsed.cart.items)) {
          for (const line of parsed.cart.items) {
            const shaped = cartToolItemToProductShape(line);
            if (shaped) cartItems.push(shaped);
          }
        }
        const totalValue =
          parsed.cart && typeof parsed.cart.total === "number" ? parsed.cart.total : 0;
        merged.billing_confirmation = true;
        merged.intent = ["billing_confirmation"];
        const billingDetails =
          parsed.billing != null && typeof parsed.billing === "object"
            ? parsed.billing
            : {};
        if (Object.keys(billingDetails).length > 0) {
          saveCheckoutPrefill(billingDetails);
          merged.billing = billingDetails;
        }
        if (Array.isArray(parsed.lines) && parsed.lines.length > 0) {
          merged.lines = parsed.lines;
        }
        if (parsed.totals != null && typeof parsed.totals === "object") {
          merged.totals = parsed.totals;
        }
        merged.action = {
          ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
          type: "PREPARE_BILLING",
          status: "AWAITING_CONFIRMATION",
          cart_data: cartItems,
          total_value: totalValue,
          billing_details: billingDetails,
          distinct_product_count: cartItems.length,
        };
        console.debug("[WS] prepare_billing_confirmation:", { cartItems: cartItems.length, totalValue });
      } else if (st === "empty_cart") {
        merged.billing_confirmation = false;
        merged.intent = ["billing_confirmation"];
        merged.action = {
          ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
          type: "PREPARE_BILLING",
          status: "EMPTY_CART",
        };
      } else if (st === "missing_billing") {
        merged.billing_confirmation = false;
        merged.intent = ["billing_confirmation"];
        merged.action = {
          ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
          type: "PREPARE_BILLING",
          status: "MISSING_BILLING",
        };
      }
    }

    if (toolNorm === "confirm_purchase") {
      const st = parsed.status;
      if (st === "completed") {
        const summary = parsed.summary != null && typeof parsed.summary === "object"
          ? parsed.summary : {};
        const purchasedItems = [];
        if (Array.isArray(summary.items)) {
          for (const line of summary.items) {
            const shaped = cartToolItemToProductShape(line);
            if (shaped) purchasedItems.push(shaped);
          }
        }
        merged.purchase_completed = true;
        merged.intent = ["confirm_purchase"];
        merged.summary = summary;
        if (Array.isArray(summary.lines) && summary.lines.length > 0) {
          merged.lines = summary.lines;
        }
        if (summary.totals != null && typeof summary.totals === "object") {
          merged.totals = summary.totals;
        }
        merged.action = {
          ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
          type: "PURCHASE_COMPLETE",
          status: "SUCCESS",
          total_amount: typeof summary.total === "number" ? summary.total : 0,
          purchased_items: purchasedItems,
          article_ids: Array.isArray(summary.article_ids) ? summary.article_ids : [],
        };
        console.debug("[WS] confirm_purchase completed:", { total: summary.total, items: purchasedItems.length });
      } else if (st === "failed" || st === "empty_cart") {
        merged.purchase_completed = false;
        merged.intent = ["confirm_purchase"];
        merged.action = {
          ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
          type: "PURCHASE_FAILED",
          status: "ERROR",
          error: parsed.error ?? "Purchase could not be completed",
        };
      }
    }

    if (toolNorm === "close_virtual_try_on") {
      const st = typeof parsed.status === "string" ? parsed.status.toLowerCase() : "";
      if (st === "closed" || st === "success" || st === "ok") {
        merged.intent = ["close_virtual_try_on"];
        merged.action = {
          ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
          type: "CLOSE_VIRTUAL_TRY_ON",
          status: "SUCCESS",
        };
        console.debug("[WS] close_virtual_try_on triggered");
      }
    }

    if (toolNorm === "update_cart_quantity") {
      const st = parsed.status;
      const isUpdateSuccess =
        st === "updated" || st === "success" || st === "ok" ||
        (parsed.cart != null && st !== "not_in_cart" && st !== "error");
      if (isUpdateSuccess) {
        merged.intent = ["update_cart_quantity"];
        merged.action = {
          ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
          type: "UPDATE_CART_QUANTITY",
          status: "SUCCESS",
          article_id: parsed.article_id ?? parsed.product_id ?? parsed.id ?? null,
          ...(parsed.cart != null ? { cart: parsed.cart } : {}),
        };
        console.debug("[WS] update_cart_quantity:", { st, cart: parsed.cart });
      }
    }

    if (toolNorm === "get_order_history" || toolNorm === "order_history") {
      const orders = Array.isArray(parsed.orders) ? parsed.orders : [];
      merged.intent = ["order_history"];
      merged.action = {
        ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
        type: "ORDER_HISTORY",
        status: "SUCCESS",
        orders,
        total_orders:
          typeof parsed.total_orders === "number" ? parsed.total_orders : orders.length,
      };
      console.debug("[WS] order_history:", { count: orders.length });
    }

    if (toolNorm === "session_restore" || toolNorm === "cache_sync") {
      const actionType = toolNorm === "session_restore" ? "SESSION_RESTORE" : "CACHE_SYNC";
      merged.intent = [toolNorm];
      merged.action = {
        ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
        type: actionType,
        status: "SUCCESS",
        ...(parsed.cart != null ? { cart: parsed.cart } : {}),
        ...(parsed.wishlist != null ? { wishlist: parsed.wishlist } : {}),
      };
      console.debug(`[WS] ${toolNorm} applied`);
    }

    if (toolNorm === "start_virtual_try_on") {
      applyVirtualTryOnIntentToMerged(
        /** @type {Record<string, unknown>} */ (merged),
        /** @type {Record<string, unknown>} */ (parsed)
      );
    }

    // Product-list tools: search_products, get_new_arrivals, get_recommendations, etc.
    // Extract products from whichever field the backend uses and set a PRODUCT_LIST action
    // so the cart-fallback logic in buildHttpShapedResponseFromWs is suppressed.
    if (PRODUCT_LIST_TOOL_NAMES.has(toolNorm)) {
      const rawList =
        (Array.isArray(parsed.products) && parsed.products.length ? parsed.products : null) ||
        (Array.isArray(parsed.items)    && parsed.items.length    ? parsed.items    : null) ||
        (Array.isArray(parsed.results)  && parsed.results.length  ? parsed.results  : null) ||
        (Array.isArray(parsed.data)     && parsed.data.length     ? parsed.data     : null) ||
        [];
      if (rawList.length > 0) {
        products = rawList;
      }
      // Set a typed action so hasSpecificAction=true and the VIEW_CART fallback is skipped.
      // Do NOT set merged.intent — empty intent + products navigates to /recommendations.
      merged.action = {
        ...(merged.action != null && typeof merged.action === "object" ? merged.action : {}),
        type: "PRODUCT_LIST",
        status: "SUCCESS",
      };
      console.debug(`[WS] ${toolNorm}: extracted ${rawList.length} products`);
    }
  }

  return { merged, products };
}

/**
 * Merge streaming `llm_response` chunks without duplicating full-message repeats.
 * Handles incremental deltas, cumulative prefixes, and identical frames.
 */
function mergeAssistantParts(parts) {
  const cleaned = (parts || [])
    .map((p) => String(p ?? ""))
    .filter((p) => p.length > 0);
  if (!cleaned.length) return "";
  if (cleaned.length === 1) return cleaned[0].trim();

  let result = cleaned[0];
  for (let i = 1; i < cleaned.length; i++) {
    const next = cleaned[i];
    if (next === result) continue;
    if (next.startsWith(result)) {
      result = next;
      continue;
    }
    if (result.startsWith(next)) continue;

    let overlap = 0;
    const maxOverlap = Math.min(result.length, next.length);
    for (let o = maxOverlap; o > 0; o--) {
      if (result.endsWith(next.slice(0, o))) {
        overlap = o;
        break;
      }
    }
    result += overlap > 0 ? next.slice(overlap) : next;
  }
  return result.trim();
}

/**
 * One chat bubble per distinct `llm_response` turn.
 * Cumulative/streaming frames for the same turn update the last bubble instead
 * of adding duplicates.
 */
export function splitAssistantPartsForDisplay(parts) {
  const cleaned = (parts || [])
    .map((p) => String(p ?? "").trim())
    .filter((p) => p.length > 0);
  if (!cleaned.length) return [];

  /** @type {string[]} */
  const segments = [];

  for (const part of cleaned) {
    const trimmed = dedupeTrailingRepeatedPhrase(part);
    if (!trimmed) continue;

    if (segments.length === 0) {
      segments.push(trimmed);
      continue;
    }

    const last = segments[segments.length - 1];
    if (trimmed === last) continue;

    if (trimmed.startsWith(last)) {
      segments[segments.length - 1] = trimmed;
      continue;
    }
    if (last.startsWith(trimmed)) continue;

    segments.push(trimmed);
  }

  return segments
    .map((s) => dedupeTrailingRepeatedPhrase(s))
    .filter((s) => s.length > 0);
}

/**
 * Chat bubble texts for the UI — one entry per distinct `llm_response`
 * (e.g. tool_preamble then final answer). Never includes tool payloads.
 * Follow-up questions are appended to the last bubble only when not redundant.
 */
export function buildChatBubbleTextsFromResponse(lastResponse) {
  if (!lastResponse || typeof lastResponse !== "object") return [];

  const displayMessages = Array.isArray(lastResponse.llm_display_messages)
    ? lastResponse.llm_display_messages
        .map((s) => dedupeTrailingRepeatedPhrase(String(s ?? "").trim()))
        .filter(Boolean)
    : [];

  let segments = displayMessages.length
    ? splitAssistantPartsForDisplay(displayMessages)
    : [];

  if (!segments.length && lastResponse.conversation_text != null) {
    const single = dedupeTrailingRepeatedPhrase(
      String(lastResponse.conversation_text).trim()
    );
    if (single) segments = [single];
  }

  if (!segments.length) return [];

  const lastIdx = segments.length - 1;
  const withFollowup = formatConversationForDisplay(
    segments[lastIdx],
    lastResponse.followup
  );
  if (withFollowup && withFollowup !== segments[lastIdx]) {
    segments = [...segments.slice(0, lastIdx), withFollowup];
  }
  return segments;
}

/** Remove an exact trailing phrase repeated twice (common LLM / WS glitch). */
function dedupeTrailingRepeatedPhrase(text) {
  if (!text || typeof text !== "string") return text;
  let t = text.trimEnd();
  const minPhrase = 24;

  let changed = true;
  while (changed) {
    changed = false;
    for (let len = Math.floor(t.length / 2); len >= minPhrase; len--) {
      const phrase = t.slice(-len);
      if (t.length >= 2 * len && t.slice(-2 * len, -len) === phrase) {
        t = t.slice(0, -len).trimEnd();
        changed = true;
        break;
      }
    }
  }
  return t;
}

function normalizeDisplayWhitespace(text) {
  return String(text ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** True when a tool/HTTP follow-up restates a question the assistant body already asked. */
function isFollowupRedundantInText(baseText, followupText) {
  const normBase = normalizeDisplayWhitespace(baseText);
  const normF = normalizeDisplayWhitespace(followupText);
  if (!normF) return true;
  if (normBase.endsWith(normF) || normBase.includes(normF)) return true;

  if (normBase.includes("?") && normF.endsWith("?")) {
    const tokenize = (s) =>
      new Set(
        s
          .replace(/[?,.'";:!]/g, "")
          .split(/\s+/)
          .filter((w) => w.length > 3)
      );
    const baseWords = tokenize(normBase);
    const fWords = [...tokenize(normF)];
    if (fWords.length > 0) {
      const overlap = fWords.filter((w) => baseWords.has(w)).length;
      if (overlap / fWords.length >= 0.45) return true;
    }
  }
  return false;
}

/**
 * Final chat display string: dedupe body, append follow-up only if not already present.
 */
export function formatConversationForDisplay(conversationText, followup) {
  let text = dedupeTrailingRepeatedPhrase(String(conversationText ?? "").trim());

  let followupText = "";
  if (typeof followup === "string") {
    followupText = followup.trim();
  } else if (
    followup &&
    typeof followup === "object" &&
    typeof followup.question === "string"
  ) {
    followupText = followup.question.trim();
  }

  if (!followupText) return text;
  if (!text) return followupText;

  if (isFollowupRedundantInText(text, followupText)) {
    return text;
  }

  const needsSpace =
    text.length > 0 && !/\s$/.test(text) && !/^[,.!?]/.test(followupText);
  return text + (needsSpace ? " " : "") + followupText;
}

/** Mirror backend `_extract_text` (Gemini may send list of text blocks). */
function extractLlmContent(content) {
  if (content == null) return "";
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    const parts = [];
    for (const chunk of content) {
      if (
        chunk &&
        typeof chunk === "object" &&
        chunk.type === "text" &&
        typeof chunk.text === "string"
      ) {
        parts.push(chunk.text);
      } else if (typeof chunk === "string") {
        parts.push(chunk);
      }
    }
    return parts.join("");
  }
  return String(content);
}

function normalizeProducts(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((p) => {
      if (!p || typeof p !== "object") return null;
      const id = p.id ?? p.product_id ?? p.article_id ?? p.sku;
      if (id == null) return null;
      const img =
        p.image_uri ||
        p.image ||
        p.uri ||
        (Array.isArray(p.images) && p.images[0]) ||
        "";
      return { ...p, id: String(id), image_uri: img, image: img };
    })
    .filter(Boolean);
}

/**
 * Build the same response shape the HTTP chat route uses, using only
 * data observed on the WebSocket (tool JSON + LLM/message assistant
 * text). No synthetic filler strings or invented intent.
 *
 * @param {{ assistantParts: string[], toolResults: unknown[] }} payload
 */
export function buildHttpShapedResponseFromWs({ assistantParts, toolResults }) {
  console.debug("[WS] buildHttpShapedResponseFromWs input:", {
    toolResultCount: (toolResults || []).length,
    assistantPartCount: (assistantParts || []).length,
    toolNames: (toolResults || []).map((r) => {
      const row = r?.data ?? r;
      return row?.tool ?? row?.tool_name ?? "?";
    }),
  });

  const { merged, products: rawProducts } = mergeToolJson(toolResults);
  const displaySegments = splitAssistantPartsForDisplay(assistantParts);
  const assistantText = dedupeTrailingRepeatedPhrase(
    displaySegments.length
      ? mergeAssistantParts(displaySegments)
      : mergeAssistantParts(assistantParts)
  );

  /** Chat display text: LLM assistant output only (never tool_result bodies). */
  let displayConversationText = assistantText || null;

  const normalizedProducts = normalizeProducts(rawProducts);

  if (
    (!displayConversationText || !String(displayConversationText).trim()) &&
    normalizedProducts.length > 0
  ) {
    const first = normalizedProducts[0];
    const t =
      first && typeof first === "object"
        ? first.title ?? first.prod_name ?? first.name
        : null;
    if (typeof t === "string" && t.trim()) {
      displayConversationText = t.trim();
    }
  }

  const out = /** @type {Record<string, unknown>} */ ({ ...merged });
  /** Fallback when pass-1 merged cart keys but tool tag did not match pass-2 (`View_Cart`, etc.).
   *  Skip when clear_cart already handled it or a specific action was set. */
  const isClearCart = out._is_clear_cart === true;
  const hasSpecificAction =
    out.action != null &&
    typeof out.action === "object" &&
    !Array.isArray(out.action) &&
    /** @type {any} */ (out.action).type != null &&
    /** @type {any} */ (out.action).type !== "VIEW_CART";

  if (
    !isClearCart &&
    !hasSpecificAction &&
    !out.cart_view &&
    Array.isArray(out.items) &&
    (typeof out.items_count === "number" ||
      Object.prototype.hasOwnProperty.call(out, "total")) &&
    out.status !== "added" &&
    out.status !== "needs_size" &&
    out.status !== "size_suggested"
  ) {
    applyViewCartIntentToMerged(out, out);
  }
  // Clean internal flag before returning
  delete out._is_clear_cart;
  /** Same as cart: pass-1 merged tool JSON may lack a matching pass-2 tool tag. */
  const hasVto =
    (Array.isArray(out.intent) && out.intent.includes("virtual_try_on")) ||
    out.intent === "virtual_try_on";
  if (
    !hasVto &&
    out.product_id != null &&
    String(out.product_id).trim() !== "" &&
    (out.image_uri != null || out.product_title != null) &&
    String(out.status ?? "")
      .toLowerCase()
      .match(/^(started|success|ok)$/)
  ) {
    applyVirtualTryOnIntentToMerged(
      /** @type {Record<string, unknown>} */ (out),
      /** @type {Record<string, unknown>} */ (out)
    );
  }
  out.conversation_text = dedupeTrailingRepeatedPhrase(
    displayConversationText != null ? String(displayConversationText).trim() : ""
  );
  // Preserve one entry per distinct llm_response (preamble + final, etc.)
  out.llm_display_messages =
    displaySegments.length > 0
      ? displaySegments
      : out.conversation_text
        ? [out.conversation_text]
        : [];
  out.products = normalizedProducts;

  // NOTE: The cart→wishlist remap block that used to live here was removed.
  // The backend now has dedicated wishlist tools (add_to_wishlist, view_wishlist,
  // remove_from_wishlist) so remapping based on LLM text is incorrect — it caused
  // "add to cart" to appear as "add to wishlist" whenever the response mentioned the
  // word "wishlist" (e.g. "Added to cart! Your wishlist still has 2 items.").

  // Text-only path: agent responded about wishlist without calling any tool.
  // Detect add / remove / view and synthesize the correct action so ChatBot syncs state.
  if (
    displayConversationText &&
    /wishlist/i.test(displayConversationText) &&
    (out.action == null ||
      typeof out.action !== "object" ||
      !(/** @type {any} */ (out.action).type))
  ) {
    const wlText = displayConversationText;
    let synthesizedType;
    if (/added.*wishlist|i['']ve added.*wishlist|saved.*your wishlist|have added.*wishlist/i.test(wlText)) {
      synthesizedType = "ADD_TO_WISHLIST";
      out.intent = ["add_to_wishlist"];
    } else if (/removed.*wishlist|removed from.*wishlist|taken.*off.*wishlist/i.test(wlText)) {
      synthesizedType = "REMOVE_FROM_WISHLIST";
      out.intent = ["remove_from_wishlist"];
    } else {
      synthesizedType = "VIEW_WISHLIST";
      out.intent = ["view_wishlist"];
    }
    out.action = {
      type: synthesizedType,
      status: "SUCCESS",
      wishlist_data: null,
    };
    console.debug("[WS] wishlist intent synthesized from LLM text:", synthesizedType);
  }

  console.debug("[WS] buildHttpShapedResponseFromWs output:", {
    intent: out.intent,
    actionType: out.action?.type,
    actionStatus: out.action?.status,
    billing_confirmation: out.billing_confirmation,
    purchase_completed: out.purchase_completed,
    cart_view: out.cart_view,
    wishlist_view: !!(out.action?.type === "VIEW_WISHLIST"),
    products: Array.isArray(out.products) ? out.products.length : 0,
    conversation_text: typeof out.conversation_text === "string"
      ? out.conversation_text.slice(0, 80)
      : null,
  });

  return out;
}

/**
 * Some servers attach tool payloads only on the final `completion` frame.
 *
 * @param {unknown} frame
 * @returns {unknown[]}
 */
function extractToolResultsFromCompletionFrame(frame) {
  if (!frame || typeof frame !== "object") return [];
  const f = /** @type {Record<string, unknown>} */ (frame);
  const data =
    f.data != null && typeof f.data === "object"
      ? /** @type {Record<string, unknown>} */ (f.data)
      : null;
  /** @type {unknown[]} */
  const buckets = [
    f.tool_results,
    f.results,
    data?.tool_results,
    data?.results,
  ];
  /** @type {unknown[]} */
  const out = [];
  for (const b of buckets) {
    if (!Array.isArray(b)) continue;
    for (const entry of b) {
      if (!entry || typeof entry !== "object") continue;
      const row = coerceToolResultRecord(entry) ?? entry;
      if (row && typeof row === "object") out.push(row);
    }
  }
  return out;
}

/**
 * Run one user turn through the agent WebSocket and resolve with an
 * HTTP-shaped `lastResponse` for OrbieeFlow.
 *
 * Text: pass `message`. Voice: pass `audioBlob` (WAV); server returns `transcript`
 * then runs the agent. Resolved payload includes `user_transcription` when voice.
 */
export async function runOrbieeChatTurn({
  userId,
  deviceId,
  message,
  audioBlob = null,
  languageCode,
  queryParams = {},
  onLlmDisplayUpdate = null,
  onTranscript = null,
  /** Per-frame callback: (text, allPartsSoFar) — used by avatar / voice TTS queues. */
  onLlmResponse = null,
  /**
   * Client interaction mode attached to the wire frame:
   *   "text"   — typed chat
   *   "voice"  — chat-box voice mic (type remains "voice")
   *   "avatar" — avatar-widget mic (type remains "voice")
   */
  mode = null,
} = {}) {
  const isVoiceTurn =
    audioBlob != null &&
    (audioBlob instanceof Blob || audioBlob instanceof File);
  const trimmed = (message ?? "").toString().trim();
  if (!isVoiceTurn && !trimmed) {
    throw new Error("runOrbieeChatTurn: message or audioBlob required");
  }

  const wireMode =
    mode === "text" || mode === "voice" || mode === "avatar"
      ? mode
      : isVoiceTurn
        ? "voice"
        : "text";

  if (!hasWsAuthToken()) {
    requireWsAuth();
    throw new Error(WS_AUTH_ERROR_MESSAGE);
  }

  // Barge-in: a new turn supersedes any reply still being spoken. The server
  // stops its side too, but it cannot un-send audio already in flight.
  stopOrbieeVoicePlayback();

  let voiceBase64 = null;
  if (isVoiceTurn) {
    voiceBase64 = await blobToBase64(audioBlob);
    if (!voiceBase64) {
      throw new Error("runOrbieeChatTurn: failed to encode audio");
    }
  }

  const uid = userId != null ? String(userId) : "guest";
  const did = deviceId != null ? String(deviceId) : await getOrbieeDeviceId();

  const mergedParams = { ...queryParams };
  if (languageCode != null && languageCode !== "") {
    mergedParams.ctx_language_code = String(languageCode);
  }

  const keys = Object.keys(mergedParams);
  const qp = keys.length ? mergedParams : undefined;

  const socket = getOrCreateSocket(uid, did, qp);

  if (!socket.isConnected) {
    socket.connect();
    await waitForOpen(socket);
  }

  if (!socket.isConnected) {
    throw new Error("WebSocket is not connected");
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    /** @type {{ text: string, language_code: string | null, audio_duration_secs?: number | null, input_mode?: string } | null} */
    let capturedTranscript = null;
    /**
     * Two separate buckets so we never merge streaming llm_response chunks
     * with the final assistant message echo. At build time we prefer
     * llmResponseParts (streaming); messageParts is the fallback for backends
     * that only emit a single `message` frame without `llm_response` frames.
     */
    const llmResponseParts = [];
    const messageParts = [];
    const toolResults = [];
    let llmStartSeen = false;
    /** Incremented on every `tool_call` frame so onCompletion knows how many results to wait for. */
    let toolCallsObserved = 0;
    /** Maps call_id → tool name so tool_result frames without a `tool` field can be enriched. */
    const callIdToTool = new Map();
    /** All tool names seen in tool_call frames (regardless of call_id presence). */
    const toolsObserved = [];

    /** @type {ReturnType<typeof setTimeout> | null} */
    let completionFlushTimer = null;
    /** @type {ReturnType<typeof setTimeout> | null} */
    let closeGraceTimer = null;
    /** Set when a `completion` frame arrives; flush may still be polling for tool_results. */
    let completionSeen = false;

    /** @type {() => void} */
    let removeAll = () => {};

    const cancelCloseGrace = () => {
      if (closeGraceTimer != null) {
        clearTimeout(closeGraceTimer);
        closeGraceTimer = null;
      }
    };

    const finishErr = (err) => {
      if (settled) return;
      settled = true;
      cancelCloseGrace();
      removeAll();
      reject(err);
    };

    const finishOk = (val) => {
      if (settled) return;
      settled = true;
      cancelCloseGrace();
      removeAll();
      resolve(val);
    };

    const emitLlmDisplayUpdate = () => {
      if (typeof onLlmDisplayUpdate !== "function") return;
      try {
        const parts =
          llmResponseParts.length > 0 ? llmResponseParts : messageParts;
        // One chat bubble per distinct llm_response (tool_preamble, then final).
        onLlmDisplayUpdate(splitAssistantPartsForDisplay(parts));
      } catch (err) {
        console.warn("[WS] onLlmDisplayUpdate threw", err);
      }
    };

    const applyTranscript = (rawData) => {
      const norm = normalizeTranscriptData(rawData);
      if (!norm) return;
      capturedTranscript = norm;
      if (typeof onTranscript === "function") {
        try {
          onTranscript(norm);
        } catch (err) {
          console.warn("[WS] onTranscript threw", err);
        }
      }
    };

    const onTranscriptFrame = (data) => {
      applyTranscript(data);
    };

    const onMsg = (data) => {
      if (data?.source === "user" && data.content) {
        applyTranscript({ text: data.content, language_code: languageCode });
      }
      if (data?.source === "assistant" && data.content) {
        // Connect-greeting can race a fast first turn. Skip it so it is not
        // treated as the reply. After llm_start, assistant messages are the turn.
        if (!llmStartSeen && socket._streaming && consumeSessionWelcome(data)) {
          return;
        }
        messageParts.push(String(data.content));
        if (llmResponseParts.length === 0) {
          emitLlmDisplayUpdate();
        }
      }
    };

    const onAgent = (frame) => {
      if (frame?.type === "llm_start") {
        llmStartSeen = true;
      }
      if (frame?.type === "llm_response" && frame.data != null) {
        const text = extractLlmContent(frame.data.content ?? frame.data.text);
        if (text.trim()) {
          llmResponseParts.push(text);
          emitLlmDisplayUpdate();
          // Per-frame callback: lets callers (e.g. the real-time avatar) react
          // to each individual llm_response as it streams in, instead of
          // waiting for the completion frame.
          if (typeof onLlmResponse === "function") {
            try {
              onLlmResponse(text, llmResponseParts.slice());
            } catch (err) {
              console.warn("[WS] onLlmResponse threw", err);
            }
          }
        }
      }
      if (frame?.type === "tool_call") {
        toolCallsObserved += 1;
        const callId = frame.data?.call_id;
        const toolName = frame.data?.tool;
        if (toolName != null) {
          // Track every tool name so we can synthesize missing tool_result entries.
          toolsObserved.push(String(toolName));
        }
        if (callId != null && toolName != null) {
          callIdToTool.set(String(callId), String(toolName));
        }
      }
      if (frame?.type === "tool_result") {
        const payload =
          frame.data != null && typeof frame.data === "object"
            ? frame.data
            : frame;
        let row =
          coerceToolResultRecord(
            payload && typeof payload === "object" ? payload : null
          ) ?? (payload && typeof payload === "object" ? payload : null);
        if (row && typeof row === "object") {
          // Enrich with tool name from the matching tool_call frame when missing
          if (!row.tool) {
            const callId = row.call_id ?? payload?.call_id;
            if (callId != null) {
              const matched = callIdToTool.get(String(callId));
              if (matched) row = { ...row, tool: matched };
            }
          }
          toolResults.push(row);
        }
      }
    };

    /**
     * Build the HTTP-shaped response from frames collected so far.
     * @param {unknown} [completionFrameForTools]
     */
    const buildTurnResponse = (completionFrameForTools = null) => {
      const fromCompletion = completionFrameForTools
        ? extractToolResultsFromCompletionFrame(completionFrameForTools)
        : [];
      const allToolResults =
        fromCompletion.length > 0
          ? [...toolResults, ...fromCompletion]
          : toolResults;

      const resultToolNorms = new Set(
        allToolResults.map((r) =>
          normalizeAgentToolName(typeof r?.tool === "string" ? r.tool : "")
        )
      );
      const synthetic = [];
      for (const toolName of toolsObserved) {
        const norm = normalizeAgentToolName(toolName);
        if (!resultToolNorms.has(norm)) {
          synthetic.push({ tool: toolName, result: null });
          resultToolNorms.add(norm);
        }
      }
      const finalToolResults =
        synthetic.length > 0 ? [...allToolResults, ...synthetic] : allToolResults;

      const assistantParts =
        llmResponseParts.length > 0 ? llmResponseParts : messageParts;
      const lastResponse = buildHttpShapedResponseFromWs({
        assistantParts,
        toolResults: finalToolResults,
      });
      if (capturedTranscript) {
        lastResponse.user_transcription = capturedTranscript.text;
        lastResponse.transcription = capturedTranscript.text;
        if (capturedTranscript.language_code) {
          lastResponse.language_code = capturedTranscript.language_code;
        }
      }
      return lastResponse;
    };

    const hasTurnContent = () =>
      llmResponseParts.length > 0 ||
      messageParts.length > 0 ||
      toolResults.length > 0 ||
      toolsObserved.length > 0;

    const onCompletion = (frame) => {
      if (settled) return;
      completionSeen = true;
      cancelCloseGrace();

      const status =
        typeof frame?.status === "string" ? frame.status.trim().toLowerCase() : "";

      if (status === "error") {
        finishErr(new Error(frame.reason || "Agent turn failed"));
        return;
      }

      // Accept complete, idle, stopped — some agent builds omit status or use idle.
      if (
        status &&
        status !== "complete" &&
        status !== "idle" &&
        status !== "stopped" &&
        status !== "ok" &&
        status !== "success"
      ) {
        console.warn("[WS turn] unexpected completion status", frame);
      }

      console.debug("[WS turn] completion frame", {
        status: frame?.status,
        tool_calls: frame?.tool_calls,
        llm_calls: frame?.llm_calls,
        reason: frame?.reason,
      });

      const completionFrame = frame;
      // Server's authoritative tool-call count; fall back to what we observed from tool_call frames.
      const expectedToolResults =
        typeof frame?.tool_calls === "number"
          ? frame.tool_calls
          : toolCallsObserved;

      if (completionFlushTimer != null) {
        clearTimeout(completionFlushTimer);
        completionFlushTimer = null;
      }

      /**
       * Poll until all expected tool_result frames have arrived.
       *
       * Race: the backend often emits `completion` before all `tool_result`
       * frames reach the browser — sometimes before even the `tool_call` frame
       * (so toolCallsObserved may be 0 and frame.tool_calls may be absent).
       * Strategy:
       *  1. If expectedToolResults > 0: poll every 40 ms until count matches
       *     (up to 400 ms).
       *  2. If expectedToolResults = 0 (count unknown): do ONE safety poll of
       *     80 ms so any straggling tool_result frames from the same network
       *     burst have time to arrive. Then flush whatever we have.
       */
      let pollAttempts = 0;
      const MAX_POLLS = 10; // 10 × 40 ms = 400 ms max (for known count)
      const SAFETY_POLLS = 2; // 2 × 40 ms = 80 ms safety window (unknown count)

      const flush = () => {
        completionFlushTimer = null;
        if (settled) return;

        const fromCompletion =
          extractToolResultsFromCompletionFrame(completionFrame);
        const allToolResults =
          fromCompletion.length > 0
            ? [...toolResults, ...fromCompletion]
            : toolResults;

        // Known count: keep polling until we have all tool_results.
        if (
          expectedToolResults > 0 &&
          allToolResults.length < expectedToolResults &&
          pollAttempts < MAX_POLLS
        ) {
          pollAttempts++;
          console.debug(
            `[WS] waiting for tool_results: got ${allToolResults.length}/${expectedToolResults}, poll ${pollAttempts}`
          );
          completionFlushTimer = setTimeout(flush, 40);
          return;
        }

        // Unknown count (expectedToolResults = 0): do a few safety polls so
        // late-arriving tool_result frames that raced ahead of completion are
        // caught before we build the response.
        if (
          expectedToolResults === 0 &&
          allToolResults.length === 0 &&
          pollAttempts < SAFETY_POLLS
        ) {
          pollAttempts++;
          console.debug(
            `[WS] safety poll ${pollAttempts}/${SAFETY_POLLS} (expectedToolResults=0, no results yet)`
          );
          completionFlushTimer = setTimeout(flush, 40);
          return;
        }

        try {
          finishOk(buildTurnResponse(completionFrame));
        } catch (e) {
          finishErr(e);
        }
      };

      // First attempt: defer one tick so any already-queued tool_result
      // macrotasks from the same network burst can run first.
      completionFlushTimer = setTimeout(flush, 0);
    };

    const onServerErr = (frame) => {
      const msg = frame?.error != null ? String(frame.error) : "Server error";
      if (/empty transcript|no speech recognized/i.test(msg)) {
        finishErr(makeEmptyTranscriptError(msg));
        return;
      }
      finishErr(new Error(msg));
    };

    const onAgentErr = (frame) => {
      const msg =
        frame?.data?.error != null
          ? String(frame.data.error)
          : "Agent error";
      if (/empty transcript|no speech recognized/i.test(msg)) {
        finishErr(makeEmptyTranscriptError(msg));
        return;
      }
      finishErr(new Error(msg));
    };

    const onClose = (closeInfo) => {
      if (settled) return;

      console.debug("[WS turn] socket closed before settle", closeInfo);

      // Completion received — flush timer will finishOk; do not race it.
      if (completionSeen) return;

      cancelCloseGrace();
      closeGraceTimer = setTimeout(() => {
        closeGraceTimer = null;
        if (settled || completionSeen) return;

        if (hasTurnContent()) {
          console.warn(
            "[WS turn] completing from partial frames after socket close",
            {
              llmParts: llmResponseParts.length,
              messageParts: messageParts.length,
              toolResults: toolResults.length,
              closeCode: closeInfo?.code,
            }
          );
          try {
            finishOk(buildTurnResponse(null));
          } catch (e) {
            finishErr(e);
          }
          return;
        }

        const code = closeInfo?.code;
        const reason =
          typeof closeInfo?.reason === "string" && closeInfo.reason.trim()
            ? closeInfo.reason.trim()
            : "";
        const detail =
          code != null
            ? ` (code ${code}${reason ? `: ${reason}` : ""})`
            : reason
              ? ` (${reason})`
              : "";
        finishErr(
          new Error(
            `Connection closed before the reply finished${detail}. ` +
              "Check REACT_APP_ORBIEE_AGENT_BASE_URL — a local agent on :8000 " +
              "must implement the full /ws/chat turn (llm_response + completion)."
          )
        );
      }, 1200);
    };

    const offMsg = socket.on("message", onMsg);
    const offTranscript = socket.on("transcript", onTranscriptFrame);
    const offAgent = socket.on("agent-event", onAgent);
    const offDone = socket.on("completion", onCompletion);
    const offSrv = socket.on("server-error", onServerErr);
    const offAgentErr = socket.on("agent-error", onAgentErr);
    const offClose = socket.on("close", onClose);

    removeAll = () => {
      cancelCloseGrace();
      if (completionFlushTimer != null) {
        clearTimeout(completionFlushTimer);
        completionFlushTimer = null;
      }
      offMsg();
      offTranscript();
      offAgent();
      offDone();
      offSrv();
      offAgentErr();
      offClose();
    };

    // Attach the current screen snapshot (Frontend ↔ Agent sync contract) so
    // the agent can resolve "this"/"these"/"go back" against what's on screen.
    const screenContext = getScreenContext();
    const accepted = isVoiceTurn
      ? socket.sendVoice(voiceBase64, screenContext, wireMode)
      : socket.sendMessage(trimmed, screenContext, wireMode);
    if (!accepted) {
      finishErr(
        new Error(
          isVoiceTurn
            ? "Could not send voice (disconnected or a turn is already in progress)"
            : "Could not send message (disconnected or a turn is already in progress)"
        )
      );
    }
  });
}

/**
 * Open the shared agent WebSocket for a logged-in user and wait until the
 * connection is established (or until timeoutMs elapses).
 *
 * Uses the same default `ctx_language_code` query param as chat turns so the
 * pre-login socket is not torn down and recreated on the first message.
 *
 * @param {string|number} userId
 * @param {number} [timeoutMs=8000]
 * @param {string} [languageCode="eng"]
 */
export async function connectOrbieeChatSocket(
  userId,
  timeoutMs = 8000,
  languageCode = "eng"
) {
  if (!USE_ORBIEE_WEBSOCKET_CHAT) return;

  if (!hasWsAuthToken()) {
    requireWsAuth();
    return;
  }

  const uid = userId != null ? String(userId) : "guest";
  const did = await getOrbieeDeviceId();
  const queryParams =
    languageCode != null && String(languageCode).trim() !== ""
      ? { ctx_language_code: String(languageCode) }
      : undefined;
  const socket = getOrCreateSocket(uid, did, queryParams);

  if (socket.isConnected) return;

  // Idempotent: OPEN / CONNECTING are no-ops; RECONNECTING reconnects now.
  socket.connect();

  if (socket.isConnected) return;

  await new Promise((resolve) => {
    let settled = false;

    const settle = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      offOpen();
      offError();
    };

    // Timeout — resolve so login flow is never blocked by an offline agent.
    const timer = setTimeout(() => { settle(); resolve(); }, timeoutMs);

    const offOpen  = socket.on("open",  () => { settle(); resolve(); });
    // Also resolve on error so a refused connection doesn't hang the login.
    const offError = socket.on("error", () => { settle(); resolve(); });
    if (socket.isConnected) {
      settle();
      resolve();
    }
  });
}




